import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UploadCloud, CheckCircle, XCircle, AlertTriangle, FileSpreadsheet } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../services/api';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import PageHeader from '../components/Shared/PageHeader';
import { fmtMoney, fmtNumber, periodLabel } from '../components/CustomerBilling/format';
import { c, btn, card } from '../theme';

// Cloud Run refuses a request body over 32 MiB before it reaches the API, and
// three months of the export is ~120 MB. CSV compresses about tenfold, so the
// file is gzipped here, in the browser, and the server reads either form.
async function compress(file) {
  if (/\.gz$/i.test(file.name)) return { blob: file, name: file.name };
  if (typeof CompressionStream === 'undefined') {
    // Every browser the portal supports has it; this is for the one that does not.
    return { blob: file, name: file.name };
  }
  const blob = await new Response(file.stream().pipeThrough(new CompressionStream('gzip'))).blob();
  return { blob, name: `${file.name}.gz` };
}

const MAX_SEND_MB = 30;

export default function CustomerBillingUpload() {
  const qc = useQueryClient();
  const [file, setFile]         = useState(null);
  const [stage, setStage]       = useState(null);   // null | 'compressing' | 'uploading' | 'reading'
  const [progress, setProgress] = useState(0);
  const [preview, setPreview]   = useState(null);   // { batch_id, preview }
  const [failure, setFailure]   = useState(null);   // { error, details }
  const inputRef = useRef(null);

  const { data: batches = [], isLoading: batchesLoading } = useQuery({
    queryKey: ['billing-batches'],
    queryFn:  () => api.get('/customer-billing/batches'),
  });

  const uploadMutation = useMutation({
    mutationFn: async () => {
      setFailure(null);
      setStage('compressing');
      const { blob, name } = await compress(file);
      if (blob.size > MAX_SEND_MB * 1024 * 1024) {
        throw new Error(`Even compressed the file is ${(blob.size / 1024 / 1024).toFixed(0)} MB, over the ${MAX_SEND_MB} MB limit. Export fewer months at a time.`);
      }
      setStage('uploading');
      const fd = new FormData();
      fd.append('file', blob, name);
      return api.upload('/customer-billing/upload', fd, (p) => { setProgress(p); if (p >= 100) setStage('reading'); });
    },
    onSuccess: (data) => { setPreview(data); setStage(null); setProgress(0); },
    onError: (err) => {
      setStage(null); setProgress(0);
      setFailure({ error: err.message || 'Upload failed.', details: err.data?.details || [] });
    },
  });

  const confirmMutation = useMutation({
    mutationFn: () => api.commit('/customer-billing/upload/confirm', { batch_id: preview.batch_id }),
    onSuccess: (data) => {
      toast.success(`Committed ${data.periods.map((p) => periodLabel(p)).join(', ')} — ${fmtNumber(data.lineCount)} lines.`);
      resetAll();
      for (const key of ['billing-batches', 'billing-periods', 'billing-meta', 'billing-summary', 'billing-customers', 'billing-customer']) {
        qc.invalidateQueries({ queryKey: [key] });
      }
    },
    onError: (err) => toast.error(err.message || 'Confirm failed.'),
  });

  const cancelMutation = useMutation({
    mutationFn: () => api.post('/customer-billing/upload/cancel', { batch_id: preview.batch_id }),
    onSuccess: () => { toast('Upload discarded.'); resetAll(); qc.invalidateQueries({ queryKey: ['billing-batches'] }); },
    onError: (err) => toast.error(err.message || 'Cancel failed.'),
  });

  const [recat, setRecat] = useState(null);   // the last re-apply's result
  const recategorizeMutation = useMutation({
    mutationFn: () => api.commit('/customer-billing/recategorize', {}),
    onSuccess: (data) => {
      setRecat(data);
      toast.success(data.changed ? `Re-filed ${fmtNumber(data.changed)} lines.` : 'Every line was already filed under the current rules.');
      for (const key of ['billing-summary', 'billing-customers', 'billing-customer']) qc.invalidateQueries({ queryKey: [key] });
    },
    onError: (err) => toast.error(err.message || 'Re-applying categories failed.'),
  });
  function resetAll() {
    setFile(null); setPreview(null); setProgress(0); setStage(null); setFailure(null);
    if (inputRef.current) inputRef.current.value = '';
  }

  const p = preview?.preview;
  const busy = uploadMutation.isPending || confirmMutation.isPending || cancelMutation.isPending;
  const stageText = { compressing: 'Compressing…', uploading: `Uploading ${progress}%`, reading: 'Reading the export…' }[stage];

  return (
    <div style={s.page}>
      <PageHeader
        eyebrow="Administration"
        title="Customer Billing Upload"
        subtitle="Import the Soft-Pak sale detail export (LMSALE) as CSV. It is read and previewed first; nothing reaches Customer Billing until you confirm. Each accounting month in the file replaces that month if it was loaded before; other months are left alone."
      />

      {!preview && (
        <div style={s.card}>
          <div
            style={{ ...s.dropZone, borderColor: file ? '#86EFAC' : c.borderStrong }}
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) setFile(f); }}
          >
            <input ref={inputRef} id="billing-file" type="file" accept=".csv,.gz" style={{ display: 'none' }}
                   onChange={(e) => setFile(e.target.files[0] || null)} />
            <UploadCloud size={26} color={file ? c.success : c.textFaint} />
            <div style={s.dropText}>
              {file ? (
                <><strong style={{ color: c.success }}>{file.name}</strong><br /><span style={{ color: c.textFaint }}>{(file.size / 1024 / 1024).toFixed(1)} MB</span></>
              ) : (
                <><strong>Click to select</strong> or drag &amp; drop<br /><span style={{ color: c.textFaint }}>Soft-Pak sale detail export, .csv — large files are compressed before sending</span></>
              )}
            </div>
          </div>

          {stage && (
            <div style={s.progressWrap}>
              <div style={s.progressTrack}><div style={{ ...s.progressFill, width: stage === 'uploading' ? `${progress}%` : '100%', opacity: stage === 'uploading' ? 1 : 0.4 }} /></div>
              <span style={s.progressLabel}>{stageText}</span>
            </div>
          )}

          {failure && (
            <div style={s.errBlock} role="alert">
              <div style={s.errHead}><AlertTriangle size={14} /> {failure.error}</div>
              {failure.details?.length > 0 && (
                <ul style={s.warnList}>{failure.details.map((d) => <li key={d} style={s.errItem}>{d}</li>)}</ul>
              )}
            </div>
          )}

          <button style={{ ...s.primaryBtn, opacity: file && !busy ? 1 : 0.5 }} disabled={!file || busy}
                  onClick={() => uploadMutation.mutate()}>
            {busy ? 'Working…' : 'Read & Preview'}
          </button>
        </div>
      )}

      {p && (
        <div style={s.card}>
          <div style={s.previewHead}>
            <FileSpreadsheet size={18} color={c.navy} />
            <span style={s.previewTitle}>Preview — {p.file_name}</span>
          </div>

          <div style={s.statGrid}>
            <Stat label="Rows in file" value={fmtNumber(p.source_rows)} />
            <Stat label="Lines with an amount" value={fmtNumber(p.stored_lines)} />
            <Stat label="Comment rows skipped" value={fmtNumber(p.comment_rows)} />
            <Stat label="Other $0 rows skipped" value={fmtNumber(p.zero_rows)} />
            <Stat label="Total billed" value={fmtMoney(p.total_amount)} />
          </div>

          {p.warnings?.length > 0 && (
            <div style={s.warnBlock}>
              <div style={s.warnHead}><AlertTriangle size={14} /> Read before confirming</div>
              <ul style={s.warnList}>{p.warnings.map((w) => <li key={w} style={s.warnItem}>{w}</li>)}</ul>
            </div>
          )}

          <div style={s.twoCol}>
            <MiniTable title="By accounting month" head={['Month', 'Customers', 'Lines', 'Billed']}
                       rows={p.periods.map((x) => [periodLabel(x.period), fmtNumber(x.accounts), fmtNumber(x.lines), fmtMoney(x.amount)])} />
            <MiniTable title="By company" head={['Company', 'Billed']}
                       rows={p.companies.map((x) => [x.company, fmtMoney(x.amount)])} />
          </div>
          <MiniTable title="By charge category" head={['Category', 'Lines', 'Billed']}
                     rows={p.categories.map((x) => [x.label, fmtNumber(x.lines), fmtMoney(x.amount)])} />

          <div style={s.actionRow}>
            <button style={{ ...s.primaryBtn, opacity: busy ? 0.5 : 1 }} disabled={busy} onClick={() => confirmMutation.mutate()}>
              <CheckCircle size={15} /> {confirmMutation.isPending ? 'Committing…' : p.replaces?.length ? 'Confirm & Replace' : 'Confirm & Commit'}
            </button>
            <button style={{ ...s.secondaryBtn, opacity: busy ? 0.5 : 1 }} disabled={busy} onClick={() => cancelMutation.mutate()}>
              <XCircle size={15} /> Cancel
            </button>
          </div>
        </div>
      )}

      {/* Categories are decided when a file is read, so a change to the rules
          leaves loaded months on the old ones until this is pressed. */}
      <div style={s.card}>
        <div style={s.previewHead}>
          <span style={s.previewTitle}>Charge categories</span>
        </div>
        <p style={s.recatText}>
          Each line&apos;s charge category (overfill, contamination, distance and so on) is decided when the file is read.
          After the category rules change, re-apply them to the months already loaded. This changes no amounts.
        </p>
        <button style={{ ...s.secondaryBtn, opacity: recategorizeMutation.isPending ? 0.5 : 1 }}
                disabled={recategorizeMutation.isPending} onClick={() => recategorizeMutation.mutate()}>
          {recategorizeMutation.isPending ? 'Re-applying…' : 'Re-apply categories'}
        </button>
        {recat && recat.moves.length > 0 && (
          <ul style={{ ...s.warnList, marginTop: '12px' }}>
            {recat.moves.map((m) => <li key={m.move} style={s.recatItem}>{m.move}: {fmtNumber(m.lines)} lines</li>)}
          </ul>
        )}
      </div>

      <div style={s.logSection}>
        <div style={s.logTitle}>Recent Imports · <Link to="/customer-billing">Open Customer Billing</Link></div>
        {batchesLoading ? <LoadingSpinner /> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={s.table}>
              <thead>
                <tr>{['File', 'Status', 'Months in file', 'Months held', 'Lines', 'Billed', 'By', 'When'].map((h) => <th key={h} style={s.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {batches.map((b) => (
                  <tr key={b.id} style={s.tr}>
                    <td style={s.td} title={b.file_name}>{b.file_name?.slice(-44)}</td>
                    <td style={s.td}><span style={{ ...s.statusPill, ...(STATUS_STYLE[b.status] || STATUS_STYLE.preview) }}>{b.status}</span></td>
                    <td style={s.td}>{(b.periods_in_file || []).map((x) => periodLabel(x, { short: true })).join(', ') || '—'}</td>
                    <td style={s.td}>{b.status === 'committed' ? ((b.periods_held || []).map((x) => periodLabel(x, { short: true })).join(', ') || 'none — replaced') : '—'}</td>
                    <td style={s.tdR}>{fmtNumber(b.line_count)}</td>
                    <td style={s.tdR}>{b.total_amount === null ? '—' : fmtMoney(b.total_amount)}</td>
                    <td style={s.td}>{b.uploaded_by_name || '—'}</td>
                    <td style={s.td}>{b.created_at ? new Date(b.created_at).toLocaleString() : '—'}</td>
                  </tr>
                ))}
                {!batches.length && <tr><td colSpan={8} style={s.emptyLog}>No imports yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div style={s.stat}>
      <div style={s.statVal}>{value}</div>
      <div style={s.statLabel}>{label}</div>
    </div>
  );
}

function MiniTable({ title, head, rows }) {
  return (
    <div style={s.subBlock}>
      <div style={s.subLabel}>{title}</div>
      <div style={s.tableWrap}>
        <table style={s.table}>
          <thead><tr>{head.map((h, i) => <th key={h} style={i ? s.thR : s.th}>{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r[0]} style={s.tr}>{r.map((v, i) => <td key={head[i]} style={i ? s.tdR : s.td}>{v}</td>)}</tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const STATUS_STYLE = {
  committed: { color: c.successText, background: c.successTint },
  preview:   { color: c.info,        background: c.infoTint },
  cancelled: { color: c.textFaint,   background: c.canvas },
};

const s = {
  page:        { maxWidth: '1040px', margin: '0 auto', paddingBottom: '8px' },
  card:        { ...card.sm, padding: '16px', marginBottom: '14px' },
  dropZone:    { borderWidth: '2px', borderStyle: 'dashed', borderRadius: '10px', padding: '30px', textAlign: 'center', cursor: 'pointer', marginBottom: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' },
  dropText:    { fontSize: '12.5px', color: c.textStrong, lineHeight: 1.6 },
  progressWrap:{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' },
  progressTrack:{ flex: 1, height: '5px', background: c.border, borderRadius: '4px', overflow: 'hidden' },
  progressFill:{ height: '100%', background: c.navy, borderRadius: '4px', transition: 'width 0.2s' },
  progressLabel:{ fontSize: '10.5px', fontWeight: 600, color: c.textStrong, minWidth: '130px' },
  primaryBtn:  { ...btn.primary },
  secondaryBtn:{ ...btn.secondary },
  previewHead: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' },
  previewTitle:{ fontSize: '13px', fontWeight: 600, color: c.text },
  statGrid:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', marginBottom: '16px' },
  stat:        { ...card.sunk, padding: '11px 13px' },
  statVal:     { fontSize: '18px', fontWeight: 700, color: c.text, fontVariantNumeric: 'tabular-nums' },
  statLabel:   { fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: c.textMuted, marginTop: '3px' },
  twoCol:      { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '14px' },
  subBlock:    { marginBottom: '18px' },
  subLabel:    { fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: c.textMuted, marginBottom: '8px' },
  warnBlock:   { background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '10px', padding: '12px 14px', marginBottom: '18px' },
  warnHead:    { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 600, color: c.warningText, marginBottom: '5px' },
  warnList:    { margin: 0, paddingLeft: '18px' },
  warnItem:    { fontSize: '11.5px', color: c.warningText, lineHeight: 1.6 },
  errBlock:    { background: c.dangerTint, border: '1px solid #FCA5A5', borderRadius: '10px', padding: '12px 14px', marginBottom: '16px' },
  errHead:     { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 600, color: c.dangerText },
  errItem:     { fontSize: '11.5px', color: c.dangerText, lineHeight: 1.6 },
  tableWrap:   { border: `1px solid ${c.border}`, borderRadius: '8px', overflow: 'auto' },
  table:       { width: '100%', borderCollapse: 'collapse', fontSize: '11.5px' },
  th:          { padding: '8px 10px', background: c.surfaceSunk, borderBottom: `1px solid ${c.border}`, fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: c.textMuted, whiteSpace: 'nowrap', textAlign: 'left' },
  thR:         { padding: '8px 10px', background: c.surfaceSunk, borderBottom: `1px solid ${c.border}`, fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: c.textMuted, whiteSpace: 'nowrap', textAlign: 'right' },
  tr:          { borderBottom: `1px solid ${c.canvas}` },
  td:          { padding: '7px 10px', color: c.textStrong, whiteSpace: 'nowrap', borderBottom: `1px solid ${c.canvas}` },
  tdR:         { padding: '7px 10px', color: c.textStrong, whiteSpace: 'nowrap', textAlign: 'right', borderBottom: `1px solid ${c.canvas}`, fontVariantNumeric: 'tabular-nums' },
  actionRow:   { display: 'flex', gap: '10px', marginTop: '6px' },
  logSection:  { ...card.sm, overflow: 'hidden' },
  logTitle:    { fontSize: '13px', fontWeight: 600, color: c.text, padding: '13px 16px', borderBottom: `1px solid ${c.border}`, background: c.surfaceSunk },
  statusPill:  { display: 'inline-flex', padding: '2px 8px', borderRadius: '10px', fontSize: '10.5px', fontWeight: 500 },
  recatText:   { fontSize: '12.5px', color: c.textMuted, lineHeight: 1.5, margin: '0 0 12px' },
  recatItem:   { fontSize: '12px', color: c.textStrong, lineHeight: 1.6 },
  emptyLog:    { textAlign: 'center', padding: '24px', color: c.textFaint },
};
